import React, { useState, useContext } from 'react';
import { Button, Result, Spin, Alert, Space, Typography } from 'antd';
import { ScanOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning';
import { AuthContext } from '../../context/AuthContext';
import { setApiToken } from '../../utils/tokenStorage';
import { setApiBaseUrl } from '../../api/config';
import { registerDevice } from '../../api/services/deviceService';

const { Paragraph } = Typography;

/**
 * QR-Code Scanner Komponente für die Mobile App.
 * Scannt den QR-Code aus der Web-UI und authentifiziert den Benutzer.
 */
const QRCodeScanner = ({ onSuccess, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(null);
  const { setApiTokenAuth } = useContext(AuthContext);

  // Prüfe Kamera-Berechtigung
  const checkPermission = async () => {
    if (!Capacitor.isNativePlatform()) {
      setError('QR-Code Scanner ist nur in der mobilen App verfügbar');
      return false;
    }

    try {
      const { camera } = await BarcodeScanner.checkPermissions();

      if (camera === 'granted') {
        setPermissionGranted(true);
        return true;
      } else if (camera === 'denied') {
        setPermissionGranted(false);
        setError('Kamera-Berechtigung wurde verweigert. Bitte in den Einstellungen aktivieren.');
        return false;
      } else {
        // Berechtigung anfordern
        const result = await BarcodeScanner.requestPermissions();
        const granted = result.camera === 'granted';
        setPermissionGranted(granted);
        if (!granted) {
          setError('Kamera-Berechtigung wird benötigt um QR-Codes zu scannen.');
        }
        return granted;
      }
    } catch (err) {
      console.error('Permission check error:', err);
      setError('Fehler beim Prüfen der Kamera-Berechtigung');
      return false;
    }
  };

  // Starte den Scanner
  const startScan = async () => {
    setError(null);
    setResult(null);

    const hasPermission = await checkPermission();
    if (!hasPermission) return;

    setScanning(true);

    try {
      // Google ML Kit Barcode Scanner
      const { barcodes } = await BarcodeScanner.scan({
        formats: [BarcodeFormat.QrCode],
      });

      if (barcodes && barcodes.length > 0) {
        const rawValue = barcodes[0].rawValue;
        await processQRCode(rawValue);
      } else {
        setError('Kein QR-Code gefunden');
      }
    } catch (err) {
      console.error('Scan error:', err);
      if (err.message?.includes('canceled')) {
        // User hat abgebrochen
        setError(null);
      } else {
        setError(`Scan-Fehler: ${err.message}`);
      }
    } finally {
      setScanning(false);
    }
  };

  // Bereinige die API-URL
  const cleanApiUrlString = (url) => {
    let cleanUrl = url;
    if (cleanUrl.endsWith('/api/')) {
      cleanUrl = cleanUrl.slice(0, -5);
    } else if (cleanUrl.endsWith('/api')) {
      cleanUrl = cleanUrl.slice(0, -4);
    }
    return cleanUrl.replace(/\/+$/, '');
  };

  // Verarbeite den gescannten QR-Code
  const processQRCode = async (rawValue) => {
    try {
      // Parse JSON aus QR-Code
      const data = JSON.parse(rawValue);

      // Prüfe ob es ein Reconnect-QR-Code ist (ohne Token)
      const isReconnectQr = data.requires_auth === true && !data.token;

      if (!isReconnectQr && (!data.token || !data.api_url)) {
        throw new Error('Ungültiger QR-Code: Token oder API-URL fehlt');
      }

      if (!data.api_url) {
        throw new Error('Ungültiger QR-Code: API-URL fehlt');
      }

      // Bereinige die API-URL
      const cleanApiUrl = cleanApiUrlString(data.api_url);

      console.log('QR-Code gescannt:', {
        api_url_original: data.api_url,
        api_url_clean: cleanApiUrl,
        user_id: data.user_id,
        username: data.username,
        token: data.token ? data.token.substring(0, 8) + '...' : '(nicht enthalten)',
        isReconnect: isReconnectQr,
      });

      // API-URL setzen (bereinigte Version)
      await setApiBaseUrl(cleanApiUrl);

      if (isReconnectQr) {
        // Reconnect-QR-Code: Nur API-URL setzen, kein Token
        setResult({
          success: true,
          username: data.username,
          api_url: cleanApiUrl,
          isReconnect: true,
          message: 'API-URL wurde aktualisiert. Bitte melden Sie sich an.',
        });

        // Callback aufrufen (navigiert zur Login-Seite oder Dashboard falls bereits eingeloggt)
        if (onSuccess) {
          setTimeout(() => {
            onSuccess({ ...data, isReconnect: true });
          }, 1500);
        }
      } else {
        // Normaler QR-Code mit Token
        await setApiToken(data.token);

        // Auth-Context aktualisieren
        if (setApiTokenAuth) {
          await setApiTokenAuth(data.token, data.username);
        }

        // Registriere Device für Auto-Login beim nächsten Start
        try {
          const registered = await registerDevice(data.token);
          if (registered) {
            console.log('Device registered for auto-login');
          }
        } catch (deviceError) {
          console.warn('Device registration failed (non-critical):', deviceError);
        }

        setResult({
          success: true,
          username: data.username,
          api_url: cleanApiUrl,
        });

        // Callback aufrufen
        if (onSuccess) {
          setTimeout(() => {
            onSuccess(data);
          }, 1500);
        }
      }
    } catch (err) {
      console.error('QR-Code processing error:', err);
      setResult({
        success: false,
        error: err.message,
      });
    }
  };

  // Nicht-native Plattform Info
  if (!Capacitor.isNativePlatform()) {
    return (
      <Alert
        message="Nur für Mobile App"
        description="Der QR-Code Scanner ist nur in der Android/iOS App verfügbar. Bitte installiere die App um diese Funktion zu nutzen."
        type="info"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Ergebnis anzeigen */}
      {result && (
        <Result
          status={result.success ? 'success' : 'error'}
          icon={result.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          title={
            result.success
              ? (result.isReconnect ? 'API-URL aktualisiert!' : 'Erfolgreich angemeldet!')
              : 'Fehler'
          }
          subTitle={
            result.success
              ? (result.isReconnect
                  ? result.message || `API-URL: ${result.api_url}`
                  : `Willkommen, ${result.username}!`)
              : result.error
          }
          extra={
            result.success ? null : (
              <Button type="primary" onClick={startScan}>
                Erneut versuchen
              </Button>
            )
          }
        />
      )}

      {/* Fehler anzeigen */}
      {error && !result && (
        <Alert
          message="Fehler"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Scan-Button */}
      {!result && !scanning && (
        <Space direction="vertical" style={{ width: '100%' }} align="center">
          <Paragraph type="secondary" style={{ textAlign: 'center' }}>
            Scanne den QR-Code aus der Web-Oberfläche um dich anzumelden.
          </Paragraph>

          <Button
            type="primary"
            size="large"
            icon={<ScanOutlined />}
            onClick={startScan}
            style={{ width: '200px', height: '50px' }}
          >
            QR-Code scannen
          </Button>

          {permissionGranted === false && (
            <Alert
              message="Kamera-Berechtigung erforderlich"
              description="Bitte erlaube den Kamera-Zugriff in den App-Einstellungen."
              type="warning"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}
        </Space>
      )}

      {/* Scanning in progress */}
      {scanning && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: '16px' }}>
            Halte die Kamera über den QR-Code...
          </Paragraph>
        </div>
      )}

      {/* Schließen Button */}
      {onClose && !scanning && (
        <Button
          onClick={onClose}
          style={{ marginTop: '16px', width: '100%' }}
        >
          Abbrechen
        </Button>
      )}
    </div>
  );
};

export default QRCodeScanner;
